/**
 * Карточка feedback flow для OKDoc с кнопками оценки и review redirect.
 * Дата: 16.03.2026
 */
import { ArrowUpRight, MessageSquareHeart, Star } from 'lucide-react';
import { Button } from '@/components/ui';
import { publicAppointmentActionsApi } from '@/features/okdoc/api/publicAppointmentActions';
import { AppointmentSummaryCard } from '@/features/okdoc/components/AppointmentSummaryCard';
import { okdocPatientPortals, type OkdocBrandTheme } from '@/features/okdoc/config';
import { resolveFeedbackResultCopy, resolveFeedbackSubmitActions, resolvePublicScenarioProfile } from '@/features/okdoc/utils/scenarioProfile';
import type { PublicAppointmentActionLanding } from '@/features/okdoc/types';

interface FeedbackCollectionLandingProps {
  code: string;
  landing: PublicAppointmentActionLanding;
  theme: OkdocBrandTheme;
  submitStatus: string;
  submitAction: string;
  submitError: string;
  onAction: (action: string) => void;
}

// 16.03.26: показывает экран сбора ОС и обрабатывает review-click.
export function FeedbackCollectionLanding({
  code,
  landing,
  theme,
  submitStatus,
  submitAction,
  submitError,
  onAction,
}: FeedbackCollectionLandingProps) {
  const profile = resolvePublicScenarioProfile(landing);
  const submitActions = resolveFeedbackSubmitActions(profile);
  const resolvedAction =
    submitStatus === 'success'
      ? submitAction
      : submitStatus === 'used'
        ? landing.used_action || ''
        : '';

  const resultCopy = resolveFeedbackResultCopy(profile, resolvedAction);
  const canShowResult = Boolean(resultCopy) && (submitStatus === 'success' || submitStatus === 'used');
  const positiveLabel = submitActions.find((item) => item.action === 'positive')?.label || 'Понравилось';
  const neutralLabel = submitActions.find((item) => item.action === 'neutral')?.label || 'Все нормально';
  const negativeLabel = submitActions.find((item) => item.action === 'negative')?.label || 'Не понравилось';
  // 13.03.26: feedback-кнопки тоже увели в более сдержанный карточный стиль.
  const quietSecondaryActionClass = 'w-full rounded-2xl border border-slate-200 bg-white px-5 py-4 text-base text-slate-800 shadow-sm hover:bg-slate-50 focus:ring-slate-300';
  const quietDangerActionClass = 'w-full rounded-2xl border border-rose-200 bg-white px-5 py-4 text-base text-slate-800 shadow-sm hover:bg-rose-50 focus:ring-rose-200';
  const reviewPortal = landing.tenant_slug ? okdocPatientPortals[landing.tenant_slug] : undefined;
  const reviewGiftLead = 'Оставьте отзыв, и мы подарим Вам зубную щетку или пополним телефон на 300 рублей 😊';

  return (
    <>
      <div className="mb-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
        <div className="flex items-center gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${theme.badge} text-white`}>
            <MessageSquareHeart className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">{profile?.badge || 'Сбор обратной связи'}</p>
            <h2 className="text-xl font-semibold text-slate-900">{profile?.title || 'Как прошёл визит?'}</h2>
          </div>
        </div>
        {profile?.subtitle && <p className="mt-3 text-sm font-medium text-slate-500">{profile.subtitle}</p>}
        {reviewPortal && (
          <p className="mt-4 text-base font-medium leading-6 text-slate-900">{reviewGiftLead}</p>
        )}
        <p className="mt-4 text-sm leading-6 text-slate-600">
          {profile?.description ||
            'Нам важно Ваше впечатление после приёма. Выберите вариант, который лучше всего отражает Ваш опыт.'}
        </p>
      </div>

      <AppointmentSummaryCard landing={landing} />

      {canShowResult ? (
        <div className="mt-6 space-y-4 rounded-[28px] border border-emerald-100 bg-emerald-50 p-6 text-center text-emerald-900">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
            <Star className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xl font-semibold">{resultCopy.title}</p>
            <p className="mt-2 text-sm leading-6 text-emerald-800">{resultCopy.description}</p>
          </div>
          {resolvedAction === 'positive' && landing.review_url && (
            <Button
              className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
              size="lg"
              onClick={() => window.location.assign(publicAppointmentActionsApi.getTrackedRedirectUrl(code, 'review'))}
            >
              {landing.review_label || 'Оставить отзыв'}
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          <Button
            className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
            size="lg"
            onClick={() => onAction('positive')}
            isLoading={submitStatus === 'loading' && submitAction === 'positive'}
          >
            {positiveLabel}
          </Button>
          <Button
            className={quietSecondaryActionClass}
            size="lg"
            variant="secondary"
            onClick={() => onAction('neutral')}
            isLoading={submitStatus === 'loading' && submitAction === 'neutral'}
          >
            {neutralLabel}
          </Button>
          <Button
            className={quietDangerActionClass}
            size="lg"
            variant="secondary"
            onClick={() => onAction('negative')}
            isLoading={submitStatus === 'loading' && submitAction === 'negative'}
          >
            {negativeLabel}
          </Button>
        </div>
      )}

      {reviewPortal && (
        <div className="mt-6 space-y-3">
          <Button
            className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
            size="lg"
            onClick={() => window.open(reviewPortal.url, '_blank', 'noopener,noreferrer')}
          >
            {reviewPortal.buttonLabel}
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
          {(reviewPortal.extraActions || []).map((action) => (
            <Button
              key={action.url}
              className={quietSecondaryActionClass}
              size="lg"
              variant="secondary"
              onClick={() => window.open(action.url, '_blank', 'noopener,noreferrer')}
            >
              {action.label}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          ))}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          {submitError || 'Не удалось отправить ответ.'}
        </div>
      )}
    </>
  );
}
